import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Auto-login temporaire (AdSense) - à retirer après validation
const AUTOLOGIN_ENABLED = true;
const AUTOLOGIN_EMAIL = 'vince@gmail.com';
const AUTOLOGIN_PASSWORD = 'Cinema12';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      verifyToken();
      return;
    }

    if (!AUTOLOGIN_ENABLED) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const autoLogin = async () => {
      try {
        const response = await axios.post(`${API_URL}/auth/login`, {
          email: AUTOLOGIN_EMAIL,
          password: AUTOLOGIN_PASSWORD
        });
        if (cancelled) return;
        const { token: newToken, user: userData } = response.data;
        localStorage.setItem('token', newToken);
        setToken(newToken);
        setUser(userData);
      } catch (error) {
        console.error('Auto-login failed:', error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    autoLogin();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.post(`${API_URL}/auth/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data.user);
    } catch (error) {
      console.error('Token verification failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { email, password });
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Erreur de connexion' 
      };
    }
  };

  const register = async (email, password, name) => {
    try {
      const response = await axios.post(`${API_URL}/auth/register`, { email, password, name });
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Erreur d\'inscription' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const isAdmin = () => {
    return user?.status === 'ADMIN';
  };

  const updateUser = (updatedData) => {
    setUser(prevUser => ({ ...prevUser, ...updatedData }));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, isAdmin, updateUser, token }}>
      {children}
    </AuthContext.Provider>
  );
};
